<script>
import isEmpty from 'lodash/isEmpty'

import { useTokenManager } from '@/utils/authorization/token-manager'
import { useOnboardingStore } from '@/stores/onboarding'
import { AUTH_ACTION, useAuthUtils } from '@/pages/auth/auth-utils'
import { maLogger } from '@/base/logger'
import { RouteName } from '@/base/constants/route'
import { ErrorCode } from '@/base/constants/api-error'

import {
  MaAlert,
  MaBadge,
  MaButton,
  MaForm,
  MaFormItem,
  MaInput,
  MaLinkButton,
} from '@mobileaction/action-kit'
import MaTextWithUnderStrike from './MaTextWithUnderStrike.vue'
import MaSocialLoginRowRegister from './MaSocialLoginRowRegister.vue'

export default {
  name: 'MaRegisterForm',
  components: {
    MaForm,
    MaFormItem,
    MaInput,
    MaButton,
    MaLinkButton,
    MaBadge,
    MaAlert,
    MaSocialLoginRowRegister,
    MaTextWithUnderStrike,
  },
  setup() {
    const onboardingStore = useOnboardingStore()
    const { processSignInRequest, getPlatform } = useAuthUtils()
    const { setUserAuth } = useTokenManager()
    return {
      onboardingStore,
      processSignInRequest,
      getPlatform,
      setUserAuth,
    }
  },
  data() {
    return {
      RouteName,
      form: {
        username: null,
        password: null,
      },
      waitingSubmit: false,
      errorMessage: null,
    }
  },
  computed: {
    rules() {
      return {
        username: [
          { required: true, message: 'Email is required' },
          { type: 'email', message: 'Email must be valid', trigger: this.form.username !== '' ? 'blur' : 'change' },
        ],
        password: [
          { required: true, message: 'Password is required' },
          {
            validator(_, v) {
              return new Promise((resolve, reject) => {
                if (!v) {
                  resolve()
                }
                if (v.length < 8) {
                  reject(new Error('Password length must be at least 8'))
                }
                if (!/.*[A-Z]/.test(v)) {
                  reject(new Error('Password must contain at least one upper case character'))
                }
                if (!/.*[a-z]/.test(v)) {
                  reject(new Error('Password must contain at least one lower case character'))
                }
                if (!/.*\d/.test(v)) {
                  reject(new Error('Password must contain at least one digit character'))
                }
                if (!/[^A-Z0-9]/i.test(v)) {
                  reject(new Error('Password must contain at least one special character'))
                }
                resolve()
              })
            },
            trigger: this.form.password !== '' ? 'blur' : 'change',
          },
        ],
      }
    },
  },
  mounted() {
    this.handleInitialErrors()
  },
  methods: {
    async submit() {
      this.waitingSubmit = true
      this.errorMessage = null
      const { forwardTo } = this.$route.query
      if (forwardTo) {
        this.form.forwardToLink = forwardTo
      }
      try {
        const userAuth = await this.$maRequests.Authentication.register({
          ...this.form,
          platform: this.getPlatform(),
          isEnterprise: window.location.hash === '#enterprise', // Not SMB user.
        })
        // signup with existing email throwing error
        if (isEmpty(userAuth)) {
          throw new Error('ALREADY_EXISTS')
        }
        userAuth.newUser = true
        this.setUserAuth(userAuth)
        await this.onboardingStore.getUserOnboardingDataFromQS(this.$route.query)
        await this.processSignInRequest(userAuth, AUTH_ACTION.REGISTER, 'email')
      }
      catch (e) {
        maLogger.error('Failed to register', e)
        let message = ''
        if (e?.message === 'ALREADY_EXISTS' || e?.errors?.includes('ALREADY_EXISTS')) {
          message = 'Please check your email inbox to access the MobileAction.'
        }
        else if (e?.errors?.includes('UNSUPPORTED_DOMAIN')) {
          message = 'Please use a work email address to register. '
        }
        this.errorMessage = message !== ''
          ? message
          : (e?.errorMessage ? e.errorMessage : 'Failed to register account, please make sure your email is valid')
      }
      this.waitingSubmit = false
    },
    handleInitialErrors() {
      const { errorCode } = this.$route.query
      if (errorCode === ErrorCode.UNSUPPORTED_DOMAIN) {
        this.errorMessage = 'Please use a business email address to register with Google'
        this.$router.replace({ query: { ...this.$route.query, errorCode: undefined } })
      }
    },
  },
}
</script>

<template>
  <div class="ma-auth-register-form">
    <h1>Let’s get started</h1>
    <MaBadge
      type="secondary"
      variant="green"
    >
      Fuel Your UA Strategy With MobileAction
    </MaBadge>
    <MaAlert
      v-if="errorMessage"
      type="danger"
      variant="filled"
      :message="errorMessage"
      :closable="false"
    />
    <MaSocialLoginRowRegister :loading="waitingSubmit" class="mx-auto" />
    <MaTextWithUnderStrike dark>
      or sign up with
    </MaTextWithUnderStrike>
    <MaForm
      :model="form"
      :rules="rules"
      @finish="submit"
    >
      <MaFormItem name="username">
        <MaInput
          v-model:value="form.username"
          title="Work E-mail"
          trim
          placeholder="Enter your business email"
          size="large"
          type="email"
          autofocus
        />
      </MaFormItem>
      <MaFormItem name="password">
        <MaInput
          v-model:value="form.password"
          title="Password"
          placeholder="Set a strong password"
          hint-text="At least 8 characters, one uppercase, one lowercase, one digit and one special character"
          type="password"
          size="large"
        />
      </MaFormItem>
      <MaButton
        size="large"
        :loading="waitingSubmit"
        html-type="submit"
      >
        Create a free account
      </MaButton>
    </MaForm>
    <span class="agreement">
      By clicking sign up button, you agree to the
      <a href="https://www.mobileaction.co/terms-of-service" target="_blank">Terms of Services</a>
      and
      <a href="https://www.mobileaction.co/privacy-policy" target="_blank">Privacy Policy</a>
    </span>
    <p class="ma-login-redirect">
      Already have an account?
      <router-link
        v-slot="{ href, navigate }"
        :to="{ name: RouteName.AUTH_LOGIN, query: $route.query }"
        custom
      >
        <MaLinkButton color="blue" :href="href" @click="navigate">
          Log in
        </MaLinkButton>
      </router-link>
    </p>
  </div>
</template>

<style lang="scss" scoped>
.ma-auth-register-form {
  @apply flex flex-1 flex-col w-full md:max-w-[22.5rem] mt-8 mx-auto md:my-auto px-5 lg:px-0 min-w-[335px];
  > h1 {
    @apply text-[2rem] leading-[3rem] mb-3 text-center font-semibold;
  }
  :deep(.ma-badge) {
    @apply max-w-max mb-6 mx-auto h-auto text-center;
  }
  :deep(.ma-button) {
    @apply max-w-full;
  }
  > .ma-alert {
    @apply mb-6;
  }
  .ma-text-with-under-strike {
    @apply my-6;
    :deep(.ma-text-content) {
      @apply text-xs text-gray-400;
    }
    :deep(.ma-sep) {
      @apply bg-gray-200;
    }
  }
  form {
    @apply flex flex-col gap-1;
    :deep(.ma-form-item) {
      @apply mb-0;
    }
    :deep(.ma-button) {
      @apply w-full mt-1;
    }
  }
  .agreement {
    @apply block mt-3 mb-6 text-xs text-gray-400 text-center;
    a {
      @apply no-underline text-gray-400 font-normal;
      &:hover {
        @apply underline;
      }
    }
  }
  .ma-login-redirect {
    @apply flex items-center justify-center gap-2 mt-auto mb-8 md:mb-0 w-full text-xs text-gray-500;
  }
}
</style>
